import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {ContextualMenuInputsOption} from '@Components/contextual-menu/components/contextual-menu-inputs-option';
import {isValidClickableLink, prependProtocol} from '@Utils/url.util';
import {InputSuggestionsDropdown} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown';
import {useGetLinkInputSuggestions} from '@Hooks/poster-editor/useGetInputSuggestions';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Text, TextSize} from '@Components/text';
import styles from './item-interactivity-link-option.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {GA4EventName} from '@Libraries/ga-events';

export function ItemInteractivityLinkOption(): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemCliclableLink = useGetKeyFromActiveItem('clickableLink');

  const [isChanging, setIsChanging] = useState(false);
  const isInvalid = useRef<boolean>(false);
  const [hyperlinkValue, setHyperlinkValue] = useState(activeItem?.clickableLink);
  const linkSuggestions = useGetLinkInputSuggestions();

  if (!activeItem || (activeItem && !('clickableLink' in activeItem))) {
    throw new Error('item does not have clickable link property');
  }

  useEffect((): void => {
    isInvalid.current = false;
    setIsChanging(false);
    setHyperlinkValue(activeItemCliclableLink);
  }, [activeItemCliclableLink]);

  const onUpdateLinkContent = (value: string, undoable = true): void => {
    activeItem.updateClickableLink(value, undoable);
  };

  const onChangeHandler = (updatedValue: string): void => {
    if (!isChanging) {
      setIsChanging(true);
    }
    setHyperlinkValue(updatedValue);
  };

  const onChangeEnded = (url: string): void => {
    if (isChanging) {
      setIsChanging(false);
    }

    if (!isValidClickableLink(url)) {
      isInvalid.current = true;
      return;
    }

    const urlWithProtocol = url === '' ? url : prependProtocol(url);
    isInvalid.current = false;
    setHyperlinkValue(urlWithProtocol);
    onUpdateLinkContent(urlWithProtocol);
  };

  const showErrorState = (): boolean => {
    if (isChanging) {
      return false;
    }
    return isInvalid.current;
  };

  const getInputValue = (): string | undefined => {
    return isChanging || isInvalid ? hyperlinkValue : activeItemCliclableLink;
  };

  const getInputSelector = (): ReactElement => {
    return (
      <ContextualMenuInputsOption
        id="sticker-interactivity-link-options"
        placeholder={window.i18next.t('pmwjs_link_placeholder_2')}
        value={getInputValue()}
        hasIcon="icon-link"
        onInputChange={onChangeHandler}
        onInputChangeEnded={onChangeEnded}
        showErrorState={showErrorState()}
        errorText={window.i18next.t('pmwjs_invalid_format')}
        elementTitle={window.i18next.t('pmwjs_link_placeholder_2')}
      />
    );
  };

  return (
    <div>
      <Text val={window.i18next.t('pmwjs_link')} size={TextSize.XXSMALL} bold className="spacing-m-b-1" />
      <InputSuggestionsDropdown
        dropdownId="item-interactivity-link-dropdown"
        inputSelector={getInputSelector()}
        onSuggestionSelect={(url: string) => {
          onChangeEnded(url);
          window.PMW.gtm.trackGA4CustomEvent(GA4EventName.BUSINESS_PROFILES_DESIGN_EDITOR_SOCIAL_LINK);
        }}
        primarySuggestionsList={linkSuggestions}
        popupPosition={DROPDOWN_POSITION.FIXED}
        popupAlignment={ALIGNMENT_TYPE.BOTTOM_END}
        popupClassName={styles.mediumWidth}
        inputValue={getInputValue()}
      />
    </div>
  );
}
